import { useContext } from "react";
import { useFieldWarning } from "@/components/save/use-field-warning";
import { dateFieldError, todayIso, type DateFieldError } from "@/lib/date-instant";
import { importFieldError, type ImportFieldError } from "@/lib/import-field-validation";
import { ProfileRevealContext } from "./context";

// The editor names two of its own keys differently from the genesis field keys the cap table is keyed
// on. Everything else is identical, so the map stays at two entries.
const CAP_KEY: Record<string, string> = { summary: "bio", role: "jobTitle" };

/** The genesis field key (and therefore cap-table key) for an editor `ProfileKey`. */
export function capKeyFor(profileKey: string): string {
  return CAP_KEY[profileKey] ?? profileKey;
}

/**
 * Earliest year the birth-date picker offers. An affordance, not a rule: `dateFieldError` bounds only the
 * upper end (not in the future), because no lower bound exists in genesis or in the product, and adding
 * one to the shared date validator would also reject legitimate historical entry dates.
 */
export const BIRTH_DATE_PICKER_FLOOR = "1900-01-01";

/** The editor's date key, kept next to {@link CAP_KEY} so both mappings are found together. */
export const BIRTH_DATE_KEY = "birthday";

/**
 * Length/format warning for one base-data field, with the editor's reveal timing. Pairs with
 * <MergeFieldWarning fieldKey={capKeyFor(key)} …>.
 */
export function useProfileFieldWarning(
  capKey: string,
  value: string,
): { error: ImportFieldError | null; onBlur: () => void; onEdit: () => void } {
  const reveal = useContext(ProfileRevealContext);
  return useFieldWarning<ImportFieldError>(importFieldError(capKey, value), reveal);
}

/** Birth-date warning (a future date, or an impossible calendar date). Pairs with <MergeDateWarning>. */
export function useProfileDateWarning(
  value: string,
): { error: DateFieldError | null; onBlur: () => void; onEdit: () => void } {
  const reveal = useContext(ProfileRevealContext);
  return useFieldWarning<DateFieldError>(dateFieldError(value, todayIso()), reveal);
}

/**
 * The edited base-data keys whose value fails validation — the editor's save gate.
 *
 * Only EDITED keys are considered. An already-stored value that is over cap (an older import, a direct
 * DB write, a cap that was tightened since) must not trap the user in a profile that can never be
 * saved again; they can still fix an untouched field, and touching it brings it into scope.
 */
export function invalidBaseEdits(edits: Partial<Record<string, string>>): string[] {
  const bad: string[] = [];
  for (const [key, value] of Object.entries(edits)) {
    if (value == null) continue;
    const failed =
      key === BIRTH_DATE_KEY
        ? dateFieldError(value, todayIso()) != null
        : importFieldError(capKeyFor(key), value) != null;
    if (failed) bad.push(key);
  }
  return bad;
}
